// CredentialPrompt — paused-on-creds UI surface.
//
// Per design doc §2.5: when discovery hits 401, the run pauses and we
// show this prompt. Default to one (user, password) row; the user can
// add more rows via [+ Add another]. On Submit, all candidates are
// posted in one call; the BE worker tries them in sequence and emits
// credentials_resolved on the first hit OR credentials_exhausted after
// the last failure (in which case this prompt re-appears for another
// round, with prior inputs preserved if the parent passes them back).
//
// Used in three places:
//   - DiscoveryPanel timeline      inline at the pause point
//   - <DiscoverButton>             popover inside per-card discovery
//   - New<X>Card                   inline within the empty add-card
//
// No "common combinations" branch — per Q2 product decision the user
// is the sole source of credentials to try.

import { useState } from 'react';
import { Loader2, Plus, X, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useT } from '@/contexts/I18nContext';
import type { Credentials } from '@/api/discovery';

export interface CredentialPromptProps {
  /** Free-form device description: "NVR @ 192.168.1.65", "Router admin", etc. */
  deviceLabel: string;
  /** Optional last-tried username surfaced from credentials_exhausted context. */
  lastTriedUsername?: string;
  /** Vendor guess for placeholder hint ("admin" for Hikvision etc.). */
  vendorHint?: string;
  /** Submitting state — disables form during BE round-trip. */
  submitting?: boolean;
  /** Called with the full candidate list when user clicks Submit. */
  onSubmit: (candidates: Credentials[]) => void | Promise<void>;
  /** Called when user clicks "Skip this device". */
  onSkip: () => void | Promise<void>;
}

interface Row {
  username: string;
  password: string;
  show: boolean;
}

const ROW_DEFAULTS: Row = { username: '', password: '', show: false };

export function CredentialPrompt({
  deviceLabel,
  lastTriedUsername,
  vendorHint,
  submitting = false,
  onSubmit,
  onSkip,
}: CredentialPromptProps) {
  const { t } = useT();
  const [rows, setRows] = useState<Row[]>([
    { ...ROW_DEFAULTS, username: vendorHint ?? 'admin' },
  ]);

  const addRow = () => setRows(r => [...r, { ...ROW_DEFAULTS }]);
  const removeRow = (i: number) =>
    setRows(r => r.length === 1 ? r : r.filter((_, idx) => idx !== i));
  const updateRow = (i: number, patch: Partial<Row>) =>
    setRows(r => r.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));

  const handleSubmit = () => {
    const candidates: Credentials[] = rows
      .map(r => ({ username: r.username.trim(), password: r.password }))
      .filter(c => c.username.length > 0);
    if (candidates.length === 0) return;
    void onSubmit(candidates);
  };

  return (
    <div className="space-y-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5 dark:border-amber-700 dark:bg-amber-950/30">
      <div className="space-y-0.5">
        <div className="text-sm font-medium">
          {t('equipment.discovery.creds.title')}
        </div>
        <div className="text-xs text-muted-foreground">
          {deviceLabel}
          {lastTriedUsername && (
            <>
              {' · '}
              {t('equipment.discovery.creds.lastTried', { username: lastTriedUsername })}
            </>
          )}
        </div>
      </div>

      <div className="space-y-1.5">
        <div className="grid grid-cols-[1fr,1.4fr,auto] gap-1.5 text-xs text-muted-foreground">
          <Label className="text-xs">{t('equipment.router.username')}</Label>
          <Label className="text-xs">{t('equipment.router.password')}</Label>
          <span className="w-7" aria-hidden />
        </div>
        {rows.map((row, i) => (
          <div key={i} className="grid grid-cols-[1fr,1.4fr,auto] gap-1.5">
            <Input
              value={row.username}
              onChange={e => updateRow(i, { username: e.target.value })}
              placeholder="admin"
              disabled={submitting}
              className="font-mono text-sm h-8"
              autoComplete="off"
            />
            <div className="relative">
              <Input
                name={`cred-secret-${i}`}
                type={row.show ? 'text' : 'password'}
                value={row.password}
                onChange={e => updateRow(i, { password: e.target.value })}
                disabled={submitting}
                className="font-mono text-sm h-8 pr-7"
                autoComplete="new-password"
              />
              <button
                type="button"
                tabIndex={-1}
                onClick={() => updateRow(i, { show: !row.show })}
                disabled={submitting}
                aria-label={row.show ? t('equipment.router.hide') : t('equipment.router.show')}
                className="absolute right-1 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {row.show ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
              </button>
            </div>
            <button
              type="button"
              onClick={() => removeRow(i)}
              disabled={submitting || rows.length === 1}
              className="rounded p-1 text-muted-foreground hover:text-foreground disabled:opacity-30"
              aria-label={t('equipment.discovery.creds.removeRow')}
            >
              <X className="size-3.5" />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={addRow}
          disabled={submitting}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <Plus className="size-3" />
          {t('equipment.discovery.creds.addRow')}
        </button>
      </div>

      <div className="flex items-center justify-between gap-2 pt-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={onSkip}
          disabled={submitting}
        >
          {t('equipment.discovery.creds.skip')}
        </Button>
        <Button
          size="sm"
          onClick={handleSubmit}
          disabled={submitting || rows.every(r => !r.username.trim())}
        >
          {submitting && <Loader2 className="size-3.5 animate-spin mr-1.5" />}
          {t('equipment.discovery.creds.submit')}
        </Button>
      </div>
    </div>
  );
}
